import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, takeUntil, Subject } from 'rxjs';
import { ChatWindowComponent } from '../../chat/chat-window/chat-window.component';
import { ChannelListComponent } from '../../chat/channel-list/channel-list.component';
import { Channel } from '../models/chat.models';
import {
  ChatService,
  FooterChannelsSubTab,
} from '../services/chat.service';

export type FooterChatTab = 'chats' | 'channels' | 'contacts';
export type ConversationSize = 'default' | 'expanded';

export interface ChatNotificationToast {
  id: string;
  senderName: string;
  senderAvatar?: string | null;
  senderInitials: string;
  messageText: string;
  channelId: number;
  createdAt: Date;
  timer?: any;
}

@Component({
  standalone: true,
  selector: 'app-footer-chat-dock',
  templateUrl: './footer-chat-dock.component.html',
  styleUrls: ['./footer-chat-dock.component.scss'],
  imports: [CommonModule, FormsModule, ChannelListComponent, ChatWindowComponent],
})
export class FooterChatDockComponent implements OnInit, OnDestroy {
  @ViewChild(ChatWindowComponent)
  set chatWindowRef(component: ChatWindowComponent | undefined) {
    this._chatWindowRef = component;
    if (component && this.pendingParticipantsDialogOpen) {
      this.pendingParticipantsDialogOpen = false;
      component.openParticipantsDialog();
    }
  }

  get chatWindowRef(): ChatWindowComponent | undefined {
    return this._chatWindowRef;
  }

  panelOpen = false;
  activeTab: FooterChatTab = 'chats';
  channelsSubTabPreference: FooterChannelsSubTab = 'group';
  activeChannel: Channel | null = null;
  activeChannelId: number | null = null;
  // chatUnread = 0;
  searchQuery = '';
  conversationSize: ConversationSize = 'default';
  actionsMenuOpen = false;

  userName = '';
  userInitials = '';

  activeToasts: ChatNotificationToast[] = [];

  // private chatUnreadSub?: Subscription;
  private _chatWindowRef?: ChatWindowComponent;
  private pendingParticipantsDialogOpen = false;
  directUnread = 0;
  channelUnread = 0;
  connectionStatus: 'online' | 'offline' | 'connecting' | 'connected' | 'reconnecting' =
    'connecting';
  private unreadSubs: Subscription[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    public router: Router,
    private chatService: ChatService
  ) { }

  ngOnInit(): void {
    this.loadUserInfo();
    const savedSize = sessionStorage.getItem('dock_conv_size');
    if (savedSize === 'expanded') {
      this.conversationSize = 'expanded';
    }

    this.chatService.openFooterChat$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        this.panelOpen = true;
        if (request.channelsSubTab) {
          this.channelsSubTabPreference = request.channelsSubTab;
        }

        if (request.channelId) {
          this.openChannelById(request.channelId);
        }
      });

    this.chatService.inAppNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ message, channelId }) => {
        this.handleInAppNotification(message, channelId);
      });

    if (this.chatService.hasAuthContext()) {
      this.chatService.connectWebSocket();
      // Keep unread badges alive while the channel-list panel is closed.
      this.chatService.bootstrapUnreadTracking();
      this.unreadSubs.push(
        this.chatService.directUnread$.subscribe(count => {
          this.directUnread = count;
        }),
        this.chatService.channelUnread$.subscribe(count => {
          this.channelUnread = count;
        }),
        this.chatService.connectionStatus$.subscribe((status) => {
          this.connectionStatus = status;
        })
      );
    }
  }

  ngOnDestroy(): void {
    // this.chatUnreadSub?.unsubscribe();
    this.unreadSubs.forEach(sub => sub.unsubscribe());
    if (this.activeChannelId) {
      this.chatService.setActiveChannel(null);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private openChannelById(channelId: number): void {
    this.chatService.getChannelDetail(channelId).subscribe({
      next: (detail) => {
        const type = detail.channel.channel_type;
        if (type === 'direct') {
          this.activeTab = 'chats';
        } else if (type === 'entity_linked') {
          this.activeTab = 'channels';
          this.channelsSubTabPreference = 'entity_linked';
        } else if (type === 'group') {
          this.activeTab = 'channels';
          this.channelsSubTabPreference = 'group';
        }
        this.onChannelSelected(detail.channel);
      }
    });
  }

  private handleInAppNotification(message: any, channelId: number): void {
    const senderName = message?.sender_name || 'SD books';
    let textContent = message?.message_text || '';

    if (textContent && textContent.includes('<')) {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = textContent;
      textContent = (tmp.textContent || tmp.innerText || textContent.replace(/<[^>]*>?/gm, '')).trim();
    }

    if (!textContent && message?.attachment_name) {
      textContent = `📎 Attachment: ${message.attachment_name}`;
    } else if (!textContent && message?.message_type === 'file') {
      textContent = '📎 Sent a file';
    }

    if (textContent.length > 100) {
      textContent = textContent.substring(0, 97) + '...';
    }

    const toastId = 'toast_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6);

    const toast: ChatNotificationToast = {
      id: toastId,
      senderName,
      senderAvatar: message?.sender_avatar || null,
      senderInitials: this.getToastInitials(senderName),
      messageText: textContent,
      channelId,
      createdAt: new Date()
    };

    toast.timer = setTimeout(() => {
      this.dismissToast(toastId);
    }, 10000);

    this.activeToasts.unshift(toast);
    if (this.activeToasts.length > 3) {
      const removed = this.activeToasts.pop();
      if (removed?.timer) {
        clearTimeout(removed.timer);
      }
    }
  }

  dismissToast(toastId: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    const idx = this.activeToasts.findIndex((t) => t.id === toastId);
    if (idx !== -1) {
      if (this.activeToasts[idx].timer) {
        clearTimeout(this.activeToasts[idx].timer);
      }
      this.activeToasts.splice(idx, 1);
    }
  }

  onToastClick(toast: ChatNotificationToast): void {
    this.dismissToast(toast.id);
    this.panelOpen = true;
    this.openChannelById(toast.channelId);
  }

  private getToastInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  get showDock(): boolean {
    const url = this.router.url;
    return !(
      url.includes('/maintainance/maintainance-page') ||
      url.includes('/contacts/contact-detail') ||
      url.includes('/error/error-page') ||
      url.includes('/login') ||
      url.includes('/chat')
    );
  }

  openTab(tab: FooterChatTab): void {
    if (this.panelOpen && this.activeTab === tab) {
      this.minimizePanel();
      return;
    }
    if (tab === 'channels') {
      this.channelsSubTabPreference = 'group';
    }
    this.activeTab = tab;
    this.panelOpen = true;
  }

  minimizePanel(): void {
    this.panelOpen = false;
  }

  closeAll(): void {
    this.panelOpen = false;
    this.closeChatWindow();
  }

  onChannelSelected(channel: Channel): void {
    this.activeChannel = channel;
    this.activeChannelId = channel.id;
    this.conversationMinimized = false;
    this.chatService.setActiveChannel(channel.id, true);
    this.chatService.clearUnread(channel.id);
  }

  closeChatWindow(): void {
    this.activeChannel = null;
    this.activeChannelId = null;
    this.conversationSize = 'default';
    this.conversationMinimized = false;
    this.actionsMenuOpen = false;
    this.chatService.setActiveChannel(null, false);
  }

  toggleExpand(): void {
    this.conversationSize = this.conversationSize === 'expanded' ? 'default' : 'expanded';
    sessionStorage.setItem('dock_conv_size', this.conversationSize);
  }

  conversationMinimized = false;

  minimizeConversation(): void {
    this.conversationMinimized = true;
    this.actionsMenuOpen = false;

    if (this.activeChannelId) {
      this.chatService.setActiveChannel(
        this.activeChannelId,
        false
      );
    }
  }

  onMinimizedHeaderClick(): void {
    if (!this.conversationMinimized) {
      return;
    }

    this.conversationMinimized = false;

    if (this.activeChannelId) {
      this.chatService.setActiveChannel(
        this.activeChannelId,
        true
      );

      this.chatService.clearUnread(
        this.activeChannelId
      );

      setTimeout(() => {
        this.chatWindowRef?.markCurrentChannelRead();
      }, 0);
    }
  }

  toggleActionsMenu(): void {
    this.actionsMenuOpen = !this.actionsMenuOpen;
  }

  closeActionsMenu(): void {
    this.actionsMenuOpen = false;
  }

  getChannelTitle(): string {
    if (this.chatWindowRef) {
      return this.chatWindowRef.getChannelTitle();
    }
    if (!this.activeChannel) return '';
    if (this.activeChannel.channel_name) return this.activeChannel.channel_name;
    if (this.activeChannel.channel_type === 'direct') return 'Direct Message';
    return `Channel #${this.activeChannel.id}`;
  }

  get conversationAvatarInitials(): string {
    if (this.chatWindowRef) {
      return this.chatWindowRef.headerAvatarInitials;
    }
    const title = this.getChannelTitle();
    const parts = title
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'U';
  }

  get showParticipantLink(): boolean {
    return !!this.activeChannel &&
      (this.activeChannel.channel_type === 'group' ||
        this.activeChannel.channel_type === 'entity_linked') &&
      !this.conversationMinimized;
  }

  get participantCountLabel(): string {
    if (this.chatWindowRef) {
      return this.chatWindowRef.participantCountLabel;
    }
    const count = this.activeChannel?.member_count ?? 0;
    return `${count} participant${count === 1 ? '' : 's'}`;
  }

  openParticipantsDialog(): void {
    if (this.chatWindowRef) {
      this.chatWindowRef.openParticipantsDialog();
      return;
    }
    this.pendingParticipantsDialogOpen = true;
  }

  get connectionStatusLabel(): string {
    switch (this.connectionStatus) {
      case 'connected':
        return 'Available';
      case 'offline':
        return 'Offline';
      case 'reconnecting':
        return 'Reconnecting…';
      case 'connecting':
      case 'online':
        return 'Connecting…';
      default:
        return 'Available';
    }
  }

  private loadUserInfo(): void {
    try {
      const raw = localStorage.getItem('optplanner_user');
      if (!raw) return;
      const user = JSON.parse(raw);
      const first = user.firstName || user.first_name || '';
      const last = user.lastName || user.last_name || '';
      this.userName = `${first} ${last}`.trim() || 'User';
      this.userInitials =
        `${(first || 'U').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || 'U';
    } catch {
      this.userName = 'User';
      this.userInitials = 'U';
    }
  }
}
